import { z } from 'zod';
import type { TechnicalProgress } from './presalesProgress';

// ---------------------------------------------------------------------------
// Deal documents: files uploaded on a deal are transferred to the company's Microsoft 365 storage, into
// "CRM Projects/{Deal ID} - {Deal Name}/" and its category subfolders; the CRM keeps a linked record.
// This is upload-to-storage plus linked access: edits made in Microsoft 365 are not synced back.
// ---------------------------------------------------------------------------

export const DOC_CATEGORIES = [
  { value: 'tender', label: 'Tender Documents', folder: '01 Tender Documents', original: true },
  { value: 'boq_drawings', label: 'BOQ and Drawings', folder: '02 BOQ and Drawings', original: true },
  { value: 'clarifications', label: 'Clarifications', folder: '03 Clarifications', original: true },
  { value: 'presales_working', label: 'Pre-Sales Working Files', folder: '04 Pre-Sales Working Files', original: false },
  { value: 'supplier_quotations', label: 'Supplier Quotations', folder: '05 Supplier Quotations', original: false },
  { value: 'customer_quotations', label: 'Customer Quotations', folder: '06 Customer Quotations', original: false },
  // Pre-award submittals to the consultant, one file per revision.
  { value: 'submittals', label: 'Submittals', folder: '07 Submittals', original: false },
] as const;
export type DocCategory = (typeof DOC_CATEGORIES)[number]['value'];
const CATEGORY_VALUES = DOC_CATEGORIES.map((c) => c.value) as [DocCategory, ...DocCategory[]];
/** Original tender documents from the customer (new revisions after handoff are flagged to pre-sales). */
export const isOriginalCategory = (c: string) => DOC_CATEGORIES.some((x) => x.value === c && x.original);

export const DOC_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'uploading', label: 'Uploading' },
  { value: 'verified', label: 'Verified' },
  { value: 'failed', label: 'Failed' },
] as const;
export type DocStatus = (typeof DOC_STATUSES)[number]['value'];

export const PRESALES_STATUSES = [
  { value: 'awaiting_documents', label: 'Awaiting Documents' },
  { value: 'ready', label: 'Ready for Pre-Sales' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'clarification', label: 'Clarification Required' },
  { value: 'quotation_ready', label: 'Quotation Ready' },
] as const;
export type PresalesStatus = (typeof PRESALES_STATUSES)[number]['value'];

/** Folder name for a deal, continuing the company's numbering: "SQ201 - Hotel Tower". */
export function dealFolderName(prefix: string, dealNumber: number, dealName: string): string {
  return `${safeName(prefix, 20)}${dealNumber} - ${safeName(dealName, 120) || 'Deal'}`;
}

/** The number in a deal folder name with this prefix ("SQ193- Lamar Hotel" → 193), if any. */
export function folderNumber(prefix: string, name: string): number | null {
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = new RegExp(`^\\s*${escaped}\\s*-?\\s*(\\d+)`, 'i').exec(name);
  return m ? Number(m[1]) : null;
}

/** A file or folder name OneDrive/SharePoint accepts: no " * : < > ? / \ |, no leading/trailing dots or spaces. */
export function safeName(name: string, max = 200): string {
  const printable = [...name].filter((c) => c.charCodeAt(0) >= 32).join('');
  return printable.replace(/["*:<>?/\\|#%]/g, '-').replace(/\s+/g, ' ').trim().replace(/^[.\s]+|[.\s]+$/g, '').slice(0, max).trim();
}

/**
 * A folder path inside the deal folder ("Addendum 1" or "Drawings/Power"): each level made safe, at most 8 levels
 * (deeper levels are joined into the 8th, never dropped, so files of different folders don't mix), at most 300 characters.
 */
export function safeFolderPath(path: string): string {
  const segs = path.split(/[/\\]+/).map((s) => s.trim()).filter((s) => safeName(s, 100));
  const levels = segs.length > 8 ? [...segs.slice(0, 7), segs.slice(7).join(' - ')] : segs;
  for (const max of [100, 60, 40, 25]) {
    const p = levels.map((s) => safeName(s, max)).filter(Boolean).join('/');
    if (p.length <= 300) return p;
  }
  return levels.map((s) => safeName(s, 25)).filter(Boolean).join('/').slice(0, 300);
}

/** "Tender.pdf", revision 2 → "Tender (Rev 2).pdf". */
export function revisionName(fileName: string, revision: number): string {
  if (revision <= 1) return fileName;
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? `${fileName.slice(0, dot)} (Rev ${revision})${fileName.slice(dot)}` : `${fileName} (Rev ${revision})`;
}

const MS_ID = z.string().trim().min(1).max(100);
export const m365SettingsSchema = z
  .object({
    tenantId: MS_ID,
    clientId: MS_ID,
    /** Omitted on later saves to keep the stored secret. */
    clientSecret: z.string().trim().min(1).max(500).optional(),
    destinationKind: z.enum(['sharepoint', 'onedrive']),
    siteUrl: z.string().trim().url().max(500).regex(/^https:\/\/[a-z0-9-]+\.sharepoint\.com\//i, 'Use the SharePoint site address, e.g. https://company.sharepoint.com/sites/Projects').nullable().optional(),
    libraryName: z.string().trim().min(1).max(200).nullable().optional(),
    onedriveUser: z.string().trim().email().max(255).nullable().optional(),
    rootFolder: z.string().trim().min(1).max(200).default('CRM Projects'),
    /** Folder that holds one folder per project (after a deal is won), e.g. "Danat Projects". */
    projectsFolder: z.string().trim().min(1).max(200).default('Danat Projects'),
    /** Deal folders: {prefix}{number} - {deal name}, e.g. SQ201 - Hotel Tower. */
    folderPrefix: z.string().trim().min(1).max(20).regex(/^[A-Za-z0-9_-]+$/, 'Letters and digits only, e.g. SQ').default('SQ'),
    /** Off: every file goes straight into the deal folder. On: into category subfolders. */
    useSubfolders: z.boolean().default(false),
  })
  .strict()
  .refine((v) => v.destinationKind !== 'sharepoint' || (!!v.siteUrl && !!v.libraryName), { path: ['siteUrl'], message: 'Give the SharePoint site address and the document library' })
  .refine((v) => v.destinationKind !== 'onedrive' || !!v.onedriveUser, { path: ['onedriveUser'], message: 'Give the OneDrive owner’s email address' });

export interface M365Status {
  configured: boolean;
  tenantId: string | null;
  clientId: string | null;
  secretHint: string | null;
  destinationKind: 'sharepoint' | 'onedrive' | null;
  siteUrl: string | null;
  libraryName: string | null;
  onedriveUser: string | null;
  rootFolder: string;
  projectsFolder: string;
  folderPrefix: string;
  useSubfolders: boolean;
  driveName: string | null;
  driveWebUrl: string | null;
  status: 'not_checked' | 'connected' | 'error';
  lastError: string | null;
  lastCheckedAt: string | null;
  storageTestAt: string | null;
  storageTestUrl: string | null;
  cleanupEnabled: boolean;
  cleanupEnabledAt: string | null;
  /** Server encryption key set (needed to store the client secret). */
  canStoreSecret: boolean;
}

export const docUploadSchema = z
  .object({
    category: z.enum(CATEGORY_VALUES),
    name: z.string().trim().min(1).max(255),
    sizeBytes: z.number().int().min(1),
    contentType: z.string().trim().min(1).max(255).default('application/octet-stream'),
    /** Upload as a new revision when a file of this name already exists (otherwise refused as a duplicate). */
    newRevision: z.boolean().default(false),
    /** A folder inside the deal folder (created when needed), e.g. "Addendum 1". Empty: the deal folder itself. */
    // Made safe and shortened first (an uploaded folder's long path), then checked.
    subfolder: z.preprocess((v) => (typeof v === 'string' ? (safeFolderPath(v) || null) : v), z.string().max(300).nullable()).optional(),
  })
  .strict();

export interface DealDocument {
  id: string;
  category: DocCategory;
  origin: 'crm_upload' | 'migrated' | 'folder';
  fileName: string;
  /** Folder inside the deal folder, if the file isn't directly in it. */
  subfolder: string | null;
  sizeBytes: number;
  revision: number;
  status: DocStatus;
  attempts: number;
  lastError: string | null;
  bytesSent: number;
  msName: string | null;
  msWebUrl: string | null;
  msModifiedAt: string | null;
  msModifiedBy: string | null;
  verifiedAt: string | null;
  verifyMethod: string | null;
  crmCopyRemovedAt: string | null;
  missingAt: string | null;
  /** Removed from the CRM's Quotations by pre-sales or an administrator (the file stays in OneDrive). */
  removedAt?: string | null;
  afterHandoff: boolean;
  uploadedByName: string | null;
  uploadedAt: string;
  /** The CRM still holds the file content (downloadable from the CRM). */
  hasCrmCopy: boolean;
}

/**
 * A quotation file (wherever pre-sales saved it): a PDF that is the house quotation — named EST… (the Danat quotation
 * number), or "Quotation…", or kept in a "Final Offers" / "Customer Quotations" folder. Suppliers' quotations (a
 * "Vendor / Supplier Quotations" folder) are not ours.
 */
export function isQuotationName(name: string, subfolder: string | null): boolean {
  if (!/\.pdf$/i.test(name)) return false;
  const folder = subfolder ?? '';
  if (/(^|\/)\s*(vendor|supplier)s?\s*(quotations?|quotes?)\s*(\/|$)/i.test(folder)) return false;
  return /^EST\s*\d/i.test(name) || /quot/i.test(name) || /(^|\/)\s*(final\s*offers?|customer\s*quotations?)\s*(\/|$)/i.test(folder);
}
/**
 * A deal document listed as a quotation: in OneDrive (verified, not moved away), not removed from the CRM, and in
 * Customer Quotations or named / placed by the rule above.
 */
export const isQuotationDocument = (d: Pick<DealDocument, 'category' | 'status' | 'missingAt' | 'removedAt' | 'fileName' | 'msName' | 'subfolder'>) =>
  !d.missingAt && !d.removedAt && d.status === 'verified' && (d.category === 'customer_quotations'
    || (d.category !== 'boq_drawings' && !isBoqName(d.msName ?? d.fileName, d.subfolder ?? null) && isQuotationName(d.msName ?? d.fileName, d.subfolder ?? null)));

/** A BOQ (bill of quantities), by its name or its folder: not a quotation even when "quotation" is in the name. */
export function isBoqName(name: string, subfolder: string | null): boolean {
  return /(^|[^a-z])boq([^a-z]|$)/i.test(name) || /(^|[^a-z])boq([^a-z]|$)/i.test(subfolder ?? '');
}

/** Names that differ only in spaces, underscores, dashes, dots or brackets are one file; the file type stays part of it. */
function plainName(name: string): [string, string] {
  const dot = name.lastIndexOf('.');
  const [base, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot + 1)] : [name, ''];
  return [base.toLowerCase().replace(/[\s_\-–—()[\].,]+/g, ' ').trim(), ext.toLowerCase()];
}
const REVISION = /(^|[^a-z0-9])(r|rev|revision)\s*\.?\s*(\d{1,3})(?=[^0-9]|$)/i;

/** The revision in a quotation's name ("EST2026 R7 …" → 7; "Rev 2" → 2), else null. */
export function quotationRevision(name: string): number | null {
  const m = REVISION.exec(name);
  return m ? Number(m[3]) : null;
}

/** One option of a quotation: its name without the revision ("EST2026 R7 Tower (Option 2)" and "R6 …" are one). */
export function quotationOption(name: string): string {
  const [base, ext] = plainName(name.replace(new RegExp(REVISION.source, 'gi'), '$1'));
  return `${base.replace(/\s+/g, ' ')}.${ext}`;
}

/**
 * Quotations by option: the latest revision first (highest R…, then the newest), the older revisions under it.
 * Copies (the same name with underscores instead of spaces) are counted, not listed. Pass them newest first.
 */
export function groupQuotations<T extends { fileName: string; msName: string | null }>(docs: T[]): { latest: T; older: T[]; copies: number }[] {
  const groups = new Map<string, { latest: T; older: T[]; copies: number; seen: Set<string> }>();
  const name = (d: T) => d.msName ?? d.fileName;
  // Of two copies the properly named one stays (spaces rather than underscores).
  const underscores = (d: T) => (name(d).match(/_/g) ?? []).length;
  const ranked = docs.map((d, i) => ({ d, i, rev: quotationRevision(name(d)) ?? -1 })).sort((a, b) => b.rev - a.rev || underscores(a.d) - underscores(b.d) || a.i - b.i);
  for (const { d } of ranked) {
    const copy = plainName(name(d)).join('.');
    const g = groups.get(quotationOption(name(d)));
    if (!g) { groups.set(quotationOption(name(d)), { latest: d, older: [], copies: 0, seen: new Set([copy]) }); continue; }
    if (g.seen.has(copy)) { g.copies++; continue; }
    g.seen.add(copy);
    g.older.push(d);
  }
  // Options in the order of their newest file (as passed in).
  const first = (g: { latest: T; older: T[] }) => Math.min(docs.indexOf(g.latest), ...g.older.map((d) => docs.indexOf(d)));
  return [...groups.values()].sort((a, b) => first(a) - first(b)).map(({ latest, older, copies }) => ({ latest, older, copies }));
}

/** A pre-sales costing workbook (it holds the costs): Excel named EST…, or with Pricing / Costing / Cost sheet in the name. */
export const isCostingWorkbookName = (name: string) =>
  /\.xls[xm]$/i.test(name) && !name.startsWith('~$') && /^EST|pricing|costing|cost\s*sheet/i.test(name);

export interface DealDocumentsResult {
  connected: boolean;
  folder: { webUrl: string | null; name: string; dealNumber: number | null } | null;
  /** Folders inside the deal folder that files were put in (to add more files to one). */
  subfolders: string[];
  documents: DealDocument[];
  canUpload: boolean;
  /** Can link an existing project folder (no folder yet, and allowed to upload). */
  canLinkFolder: boolean;
  /** May remove a quotation from the CRM (pre-sales on the deal, administrators; open deals only). */
  canRemoveQuotations: boolean;
  maxMb: number;
  presales: {
    status: PresalesStatus | null;
    userId: string | null;
    userName: string | null;
    /** Support engineers working on the deal with the lead. */
    support: { id: string; name: string }[];
    tenderDeadline: string | null;
    quoteDeadline: string | null;
    note: string | null;
    /** Tender deals need a tender submission deadline; On-Hand deals don't. */
    isTender: boolean;
    canHandOff: boolean;
    canWork: boolean;
    handoffs: { id: string; presalesUserName: string; scope: string; tenderDeadline: string | null; quoteDeadline: string; createdByName: string; createdAt: string; acknowledgedAt: string | null; taskId: string | null }[];
    /** Technical progress of the Pre-Sales assignment (null before the hand-off). */
    progress: TechnicalProgress | null;
  };
}

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
export const handoffSchema = z
  .object({
    /**
     * Normally empty: sales hands the deal to the pre-sales team and the pre-sales manager assigns the engineer in the
     * workbench. (An engineer may still be given directly, e.g. by the pre-sales manager.)
     */
    presalesUserId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid().nullable()).optional().default(null),
    /** Support engineers who work on the deal at the same time as the lead (optional; usually added in the workbench). */
    supportUserIds: z.array(z.string().uuid()).max(4).optional().default([]),
    /** What pre-sales should prepare (scope, systems, notes for the team). */
    scope: z.string().trim().max(4000).optional().default(''),
    /** The customer's submission deadline. Required for tenders (checked on the server against the deal). */
    tenderDeadline: z.preprocess((v) => (v === '' ? null : v), isoDate.nullable()).optional().default(null),
    /** When sales needs the quotation (optional): the pre-sales manager confirms or changes it. */
    quoteDeadline: z.preprocess((v) => (v === '' ? null : v), isoDate.nullable()).optional().default(null),
  })
  .strict()
  .refine((v) => !v.tenderDeadline || !v.quoteDeadline || v.quoteDeadline <= v.tenderDeadline, { path: ['quoteDeadline'], message: 'The quotation should be needed on or before the customer submission deadline' });

export const presalesUpdateSchema = z
  .object({
    status: z.enum(['in_progress', 'clarification', 'quotation_ready']),
    /** Required for "Clarification Required": what is missing. */
    message: z.string().trim().max(2000).optional(),
  })
  .strict()
  .refine((v) => v.status !== 'clarification' || !!v.message, { path: ['message'], message: 'Say which documents or answers are missing' });

/** The pre-sales workbench: deals handed to pre-sales, and (managers) the workload per engineer. */
export interface PresalesWorkbench {
  today: string;
  deals: {
    id: string; name: string; accountName: string | null; amount: number | null; ownerName: string; presalesUserId: string | null; presalesUserName: string | null;
    presalesStatus: PresalesStatus; priority: string | null; dealSource: string | null; isClosed: boolean; customerPoNumber: string | null; quoteDeadline: string | null; submissionDeadline: string | null;
    handedAt: string | null; acknowledgedAt: string | null; statusSince: string | null; note: string | null;
    /** When sales sent the quotation to the customer (null: not yet). */
    quoteSubmittedDate: string | null;
    /** What sales asked pre-sales to prepare (the last handoff). */
    scope: string | null;
    /** The deal's Microsoft 365 folder, the current quotation revision and how long supplier prices are valid. */
    folderUrl: string | null; currentRevision: string | null; supplierPriceExpiry: string | null;
    /** The deal fields pre-sales fills (read-only for sales), and how many confirmed Our Quoted Brands lines. */
    brandNotApplicable: boolean; brandNaReason: string | null; expectedMargin: number | null; brandLines: number;
    /** Why the costing workbook couldn't be read (the latest failed read, if no successful read came after it). */
    costingProblem: string | null;
    /** Pre-sales work left after the quotation: open customer submittal requests, submittals returned B / C / rejected. */
    openRequests: number; toRevise: number;
    /** Open High priority tasks on the deal (any assignee). */
    highTasks?: { subject: string; dueDate: string | null; ownerName: string }[];
    /** Material submittal before the quotation: preparing (due date optional) or sent (waiting for approval). */
    msubStatus: 'preparing' | 'sent' | null; msubDue: string | null; msubNote: string | null; msubRound: number; msubSentAt: string | null;
    /** Support engineers working on the deal at the same time as the lead: when added, and when they accepted. */
    support: { id: string; name: string; addedAt: string | null; acknowledgedAt: string | null; tasks?: number; openTasks?: number }[];
    /** Technical progress of the assignment (null before the hand-off). */
    progress: TechnicalProgress | null;
  }[];
  workload: {
    userId: string; name: string; toAcknowledge: number; inProgress: number; clarification: number; overdue: number;
    /** Value of the open deals in hand, and how many days the oldest one has been with pre-sales. */
    value: number; oldestDays: number | null;
  }[];
  /** Deals still waiting for their documents (coming to pre-sales next). */
  comingNext: { id: string; name: string; accountName: string | null; amount: number | null; ownerName: string; priority: string | null; createdAt: string; presalesUserName: string | null }[];
  /** Team performance, this month and last month (team and per engineer). */
  scores: { userId: string | null; name: string; period: 'this' | 'last'; handoffs: number; ackDays: number | null; turnaroundDays: number | null; ready: number; onTimePct: number | null }[];
  /** Active pre-sales engineers (to move a deal to, or to add as support). */
  engineers: { id: string; name: string }[];
  canSeeAll: boolean;
  /** May move deals between engineers (pre-sales manager, administrator). */
  canReassign: boolean;
  scope: 'mine' | 'all';
}

/**
 * A deal whose documents are already in Microsoft 365 (e.g. from the old CRM): skip "Awaiting Documents"
 * and record where pre-sales stands. An engineer is needed once pre-sales has started.
 */
export const existingDocumentsSchema = z
  .object({
    status: z.enum(['ready', 'in_progress', 'quotation_ready']),
    presalesUserId: z.string().uuid().nullable().optional(),
  })
  .strict()
  .refine((v) => v.status === 'ready' || !!v.presalesUserId, { path: ['presalesUserId'], message: 'Choose the pre-sales engineer working on it' });

/** One deal on ⋮⋮ Pre-sales → Quotations sent. Dates are days (YYYY-MM-DD), Riyadh. */
export interface QuotationSentRow {
  id: string; name: string; accountName: string | null; ownerName: string;
  /** The lead pre-sales engineer, and the support engineers on the deal. */
  presalesUserName: string | null; supportNames: string[];
  /** When the quotation was last marked Quotation ready (null: not known). */
  readyAt: string | null;
  /** When sales sent it to the customer (the deal's Quote submitted date). */
  sentOn: string | null;
  /** Sent − ready, in days (null without a ready date). */
  daysReadyToSent: number | null;
  /** Waiting view: today − ready, in days. */
  daysWaiting: number | null;
  /** The deal's current quotation revision (e.g. R1), if known. */
  revision: string | null;
  amount: number | null; stageName: string | null; isClosed: boolean; isWon: boolean;
}

/** ⋮⋮ Pre-sales → Quotations sent: quotations waiting to be sent, and those sent to the customer in a period. */
export interface QuotationsSentReport {
  view: 'waiting' | 'sent';
  /** The period of the Sent view (default: this month). */
  from: string; to: string; today: string;
  rows: QuotationSentRow[];
  /** Sent in the period, their average days ready → sent (those with a ready date), and how many wait now. */
  summary: { sent: number; avgDaysReadyToSent: number | null; waiting: number };
  /** Pre-sales manager / administrator: every engineer's deals, with an engineer filter. */
  canSeeAll: boolean;
  engineers: { id: string; name: string }[];
}
